import React, { PropsWithChildren } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import SummaryNavigation, { INavigationTrackedRouteProps } from '@components/UI/SummaryNavigation'
import { SHAREABLE_CONTENT_LIBRARY } from '@constants'
import { EventTrackingNames } from '@enums'
import { IApplicationState } from '@store'
import {
  fetchShareableContentLibrary,
  selectIsLoadingMoreMediaObjects,
  selectLoadMoreMediaObjects,
  selectNextMediaObjectId,
  selectNextShareableMediaObjectLink,
  selectPrevMediaObjectId,
  selectPrevShareableMediaObjectLink,
} from '@store/mediaObjects'
import { selectMediaObjectId, selectShareableContentLibraryToken } from '@store/router'
import ShareableContentLibraryTrackedRoute from '../TrackedRoute'

const TrackingWrapper: React.FC<PropsWithChildren<INavigationTrackedRouteProps>> = ({
  mediaObjectId,
  children,
  source,
}) => (
  <ShareableContentLibraryTrackedRoute
    eventName={EventTrackingNames.ShareableCLViewContentLibraryItem}
    source={`shareable_cl_${source}`}
    mediaObjectId={mediaObjectId}
  >
    {children}
  </ShareableContentLibraryTrackedRoute>
)

export const ShareableContentLibraryNavigation: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const dispatch = useDispatch()
  const match = useRouteMatch()
  const mediaObjectId = selectMediaObjectId(match)
  const token = selectShareableContentLibraryToken(match)
  const isLoadingMore = useSelector((state: IApplicationState) =>
    selectIsLoadingMoreMediaObjects(state, SHAREABLE_CONTENT_LIBRARY, mediaObjectId),
  )
  const prevLink = useSelector(
    (state: IApplicationState) =>
      selectPrevShareableMediaObjectLink(state, mediaObjectId, token) || undefined,
  )
  const nextLink = useSelector(
    (state: IApplicationState) =>
      selectNextShareableMediaObjectLink(state, mediaObjectId, token) || undefined,
  )
  const prevMediaObjectId = useSelector((state: IApplicationState) =>
    selectPrevMediaObjectId(state, SHAREABLE_CONTENT_LIBRARY, mediaObjectId),
  )
  const nextMediaObjectId = useSelector((state: IApplicationState) =>
    selectNextMediaObjectId(state, SHAREABLE_CONTENT_LIBRARY, mediaObjectId),
  )
  const loadMoreMediaObjects = useSelector((state: IApplicationState) =>
    selectLoadMoreMediaObjects(state, SHAREABLE_CONTENT_LIBRARY, mediaObjectId),
  )
  const effectiveMediaObjectId = isLoadingMore ? 0 : mediaObjectId ?? 0

  React.useEffect(() => {
    if (loadMoreMediaObjects) {
      dispatch(fetchShareableContentLibrary(token, true))
    }
  }, [loadMoreMediaObjects])

  return (
    <SummaryNavigation
      prevLink={prevLink}
      trackingWrapper={TrackingWrapper}
      prevTrackingProps={{
        eventName: EventTrackingNames.ShareableCLViewContentLibraryItem,
        mediaObjectId: prevMediaObjectId,
      }}
      nextTrackingProps={{
        eventName: EventTrackingNames.ShareableCLViewContentLibraryItem,
        mediaObjectId: nextMediaObjectId,
      }}
      nextLink={nextLink}
      currentId={effectiveMediaObjectId}
      loadingMore={loadMoreMediaObjects}
    >
      {children}
    </SummaryNavigation>
  )
}

export default ShareableContentLibraryNavigation
